import React, { useState, useCallback, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { journeyActions } from "../../store/journeySlice";
import "./CustomerVerification.scss";
import logo from "../../assets/logo.png";
import { useJourney } from "../../common/hooks/useJourney";
import { usePaymentReturnResume } from "../../common/hooks/usePaymentReturnResume";
import {
  verifyPanDetails,
  resolvePanVerificationMessage,
  getCustomerVerificationStageByFlow,
  generateAadhaarOtp,
  verifyAadhaarOtp,
  updateJourneyStage,
  getJourneyData,
} from "../../api/journeyService";
import NeedhelpModal from "../../common/components/NeedhelpModal";
import BrandLoader from "../../common/components/BrandLoader";
import { mergeCumulativeStagesOrdered } from "../../common/utils/journeyLeadFields";
import { slimCustomerVerificationSnapshotForPersistence } from "../../common/utils/journeyPersistenceSlim";
const PAN_REGEX = /^[A-Z]{3}P[A-Z][0-9]{4}[A-Z]$/;
const NAME_REGEX = /^[A-Za-z][A-Za-z\s.'-]{1,99}$/;

const VERHOEFF_D_TABLE = [
  [0, 1, 2, 3, 4, 5, 6, 7, 8, 9],
  [1, 2, 3, 4, 0, 6, 7, 8, 9, 5],
  [2, 3, 4, 0, 1, 7, 8, 9, 5, 6],
  [3, 4, 0, 1, 2, 8, 9, 5, 6, 7],
  [4, 0, 1, 2, 3, 9, 5, 6, 7, 8],
  [5, 9, 8, 7, 6, 0, 4, 3, 2, 1],
  [6, 5, 9, 8, 7, 1, 0, 4, 3, 2],
  [7, 6, 5, 9, 8, 2, 1, 0, 4, 3],
  [8, 7, 6, 5, 9, 3, 2, 1, 0, 4],
  [9, 8, 7, 6, 5, 4, 3, 2, 1, 0],
];

const VERHOEFF_P_TABLE = [
  [0, 1, 2, 3, 4, 5, 6, 7, 8, 9],
  [1, 5, 7, 6, 2, 8, 3, 0, 9, 4],
  [5, 8, 0, 3, 7, 9, 6, 1, 4, 2],
  [8, 9, 1, 6, 0, 4, 3, 5, 2, 7],
  [9, 4, 5, 3, 1, 2, 6, 8, 7, 0],
  [4, 2, 8, 6, 5, 7, 3, 9, 0, 1],
  [2, 7, 9, 3, 8, 0, 6, 4, 1, 5],
  [7, 0, 4, 6, 9, 1, 3, 2, 5, 8],
];

const sanitizeDigits = (value = "") => value.replace(/\D/g, "");

const formatAadhaarNumber = (value = "") =>
  sanitizeDigits(value)
    .slice(0, 12)
    .replace(/(\d{4})(?=\d)/g, "$1 - ");

const cloneForJourney = (value) => {
  if (value == null) return value;
  try {
    return JSON.parse(JSON.stringify(value));
  } catch {
    return null;
  }
};

const splitPanName = (value = "") => {
  const normalized = value.trim().replace(/\s+/g, " ");
  if (!normalized) {
    return { givenName: null, surname: null };
  }
  const parts = normalized.split(" ");
  if (parts.length === 1) {
    return { givenName: parts[0], surname: parts[0] };
  }
  return { givenName: parts[0], surname: parts.slice(1).join(" ") };
};

const normalizeSex = (gender = "") => {
  const normalized = String(gender || "")
    .trim()
    .toUpperCase();
  if (!normalized) return null;
  if (normalized === "MALE") return "M";
  if (normalized === "FEMALE") return "F";
  if (["M", "F", "O"].includes(normalized)) return normalized;
  return normalized;
};

const isValidAadhaar = (value = "") => {
  const cleaned = sanitizeDigits(value);
  if (!/^[2-9][0-9]{11}$/.test(cleaned)) {
    return false;
  }

  let checksum = 0;
  const reversedDigits = cleaned.split("").reverse().map(Number);
  reversedDigits.forEach((digit, index) => {
    checksum = VERHOEFF_D_TABLE[checksum][VERHOEFF_P_TABLE[index % 8][digit]];
  });
  return checksum === 0;
};

/** Resolve DOB from journey state (lead form, quote init, or quote result). Prefer ISO date (yyyy-MM-dd). */
const resolvePanDOB = (
  leadFormData,
  effectiveLeadResult,
  leadResult,
  quoteResult,
) => {
  const str = (v) => (v && typeof v === "string" && v.trim() ? v.trim() : null);
  if (str(leadFormData?.dateOfBirth)) return leadFormData.dateOfBirth.trim();
  if (str(leadFormData?.lifeAssuredDob))
    return leadFormData.lifeAssuredDob.trim();
  if (leadFormData && typeof leadFormData === "object") {
    const dobKey = Object.keys(leadFormData).find(
      (k) =>
        /dob|dateofbirth|date_of_birth|birthdate/i.test(k) &&
        str(leadFormData[k]),
    );
    if (dobKey) return leadFormData[dobKey].trim();
  }
  if (str(leadResult?.dateOfBirth)) return leadResult.dateOfBirth.trim();
  if (str(leadResult?.lifeAssuredDob)) return leadResult.lifeAssuredDob.trim();
  if (str(effectiveLeadResult?.dateOfBirth))
    return effectiveLeadResult.dateOfBirth.trim();
  if (str(effectiveLeadResult?.lifeAssuredDob))
    return effectiveLeadResult.lifeAssuredDob.trim();
  if (str(effectiveLeadResult?.leadInput?.lifeAssuredDob))
    return effectiveLeadResult.leadInput.lifeAssuredDob.trim();
  if (str(effectiveLeadResult?.leadInput?.dateOfBirth))
    return effectiveLeadResult.leadInput.dateOfBirth.trim();
  if (str(quoteResult?.lifeAssuredDob))
    return quoteResult.lifeAssuredDob.trim();
  if (str(quoteResult?.leadInput?.lifeAssuredDob))
    return quoteResult.leadInput.lifeAssuredDob.trim();
  if (str(quoteResult?.dateOfBirth)) return quoteResult.dateOfBirth.trim();
  return null;
};

/** Format DOB for PAN API: accepts yyyy-MM-dd or dd/MM/yyyy, returns dd/MM/yyyy. */
const formatDOBForPan = (dob) => {
  if (!dob || typeof dob !== "string") return null;
  const s = dob.trim();
  if (!s) return null;
  if (s.includes("/")) return s;
  const parts = s.split("-");
  if (parts.length === 3) {
    const [y, m, d] = parts;
    if (y?.length === 4) return `${d}/${m}/${y}`;
    return `${parts[2]}/${parts[1]}/${parts[0]}`;
  }
  return s;
};

const normalizeBooleanLike = (value) => {
  if (typeof value === "boolean") return value;
  if (typeof value === "string") {
    const v = value.trim().toLowerCase();
    if (v === "true") return true;
    if (v === "false") return false;
  }
  return Boolean(value);
};

const pickPanRestoreSource = (data) => {
  if (!data || typeof data !== "object") return null;
  let current = data;
  let best = data;
  for (let i = 0; i < 6; i += 1) {
    if (!current || typeof current !== "object") break;
    const hasPanData =
      current?.pan?.number ||
      current?.prefillData?.panNumber ||
      current?.prefillData?.panName;
    if (hasPanData) best = current;
    current =
      current?.customerVerificationSnapshot &&
      typeof current.customerVerificationSnapshot === "object"
        ? current.customerVerificationSnapshot
        : null;
  }
  return best;
};

const parseStageObject = (value) => {
  if (!value) return null;
  if (typeof value === "object") return value;
  if (typeof value === "string") {
    try {
      return JSON.parse(value);
    } catch {
      return null;
    }
  }
  return null;
};

const pickCustomerVerificationStageFromJourney = (journey) => {
  if (!journey || typeof journey !== "object") return null;
  const current = parseStageObject(journey.currentStageData) || {};
  const stages = parseStageObject(journey.stages) || {};

  const candidates = [
    ...Object.entries(current),
    ...Object.entries(stages),
  ];

  const match = candidates.find(([key, value]) => {
    const lower = String(key || "").toLowerCase();
    if (lower.includes("customerverification")) return true;
    if (lower.includes("stage 5") || lower.includes("5.0")) {
      return (
        value?.prefillData?.panNumber ||
        value?.prefillData?.panName ||
        value?.panVerified != null ||
        value?.customerVerificationSnapshot
      );
    }
    return false;
  });

  return match ? match[1] : null;
};

const CustomerVerification = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { isResuming } = usePaymentReturnResume();
  const {
    applicationNumber,
    caseId,
    paymentFlowMode,
    leadFormData,
    effectiveLeadResult,
    leadResult,
    quoteResult,
    handleCustomerVerificationComplete,
  } = useJourney();

  const [aadhaarNumber, setAadhaarNumber] = useState("");
  const [panNumber, setPanNumber] = useState("");
  const [panName, setPanName] = useState("");
  const [showHelpModal, setShowHelpModal] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState(null);
  const [aadhaarOtp, setAadhaarOtp] = useState("");
  const [aadhaarOtpSent, setAadhaarOtpSent] = useState(false);
  const [panVerified, setPanVerified] = useState(false);
  const [timer, setTimer] = useState(0);
  const [canResend, setCanResend] = useState(false);

  const aadhaarClientId = useSelector(
    (state) => state.journey.customerVerificationData?.aadhaar?.clientId,
  );

  const aadhaarVerified = useSelector(
    (state) => state.journey.customerVerificationData?.aadhaar?.verified,
  );

  const customerVerificationDataFromStore = useSelector(
    (state) => state.journey.customerVerificationData,
  );

  const isPanValid = PAN_REGEX.test(
  panNumber.trim().toUpperCase()
  );

  const panDOB = resolvePanDOB(
    leadFormData,
    effectiveLeadResult,
    leadResult,
    quoteResult,
  );
  const panDateOfBirthFormatted = formatDOBForPan(panDOB);

  const aadhaarData = useSelector(
    (state) => state.journey.customerVerificationData?.aadhaar
  );

  useEffect(() => {
    if (aadhaarData?.number) {
      setAadhaarNumber(aadhaarData.number);
    }

    if (aadhaarData?.verified) {
      setAadhaarOtpSent(false);
      setAadhaarOtp("");
    }
  }, [aadhaarData]);

  // Restore PAN fields/status from Redux-hydrated customer verification snapshot
  // when opening same URL in new tab/browser.
  useEffect(() => {
    const restoreSource = pickPanRestoreSource(customerVerificationDataFromStore);
    const prefill = restoreSource?.prefillData;
    const panFromStore =
      restoreSource?.pan?.number || prefill?.panNumber || "";
    const panNameFromStore = restoreSource?.pan?.name || prefill?.panName || "";
    const isPanVerified = normalizeBooleanLike(
      restoreSource?.pan?.verified ??
        restoreSource?.panVerified ??
        false,
    );

    if (panFromStore) setPanNumber(panFromStore);
    if (panNameFromStore) setPanName(panNameFromStore);
    if (isPanVerified) setPanVerified(true);
  }, [customerVerificationDataFromStore]);

  // Fallback for direct URL/open-in-new-tab:
  // restore PAN fields from current stage payload even when redux snapshot is missing.
  useEffect(() => {
    if (!applicationNumber) return;
      // API call only when PAN length is exactly 10
    if (!panNumber || panNumber.length !== 10) return;
 
    let cancelled = false;

    getJourneyData(applicationNumber)
      .then((journey) => {
        if (cancelled || !journey || typeof journey !== "object") return;
        const stage = pickCustomerVerificationStageFromJourney(journey);
        if (!stage || typeof stage !== "object") return;

        const prefill = stage?.prefillData || {};
        const stagePanNumber =
          stage?.pan?.number ||
          prefill?.panNumber ||
          stage?.customerVerificationSnapshot?.pan?.number ||
          "";
        const stagePanName = prefill?.panName || "";
        const stagePanVerified = normalizeBooleanLike(
          stage?.pan?.verified ??
            stage?.panVerified ??
            stage?.customerVerificationSnapshot?.pan?.verified ??
            false,
        );

        if (stagePanNumber) setPanNumber(stagePanNumber);
        if (stagePanName) setPanName(stagePanName);
        if (stagePanVerified) setPanVerified(true);

        dispatch(
          journeyActions.setCustomerVerificationData({
            ...(stage && typeof stage === "object" ? stage : {}),
            prefillData: {
              ...(prefill && typeof prefill === "object" ? prefill : {}),
              panNumber: stagePanNumber || undefined,
              panName: stagePanName || undefined,
            },
            pan: {
              ...(stage?.pan && typeof stage.pan === "object" ? stage.pan : {}),
              ...(stage?.customerVerificationSnapshot?.pan &&
              typeof stage.customerVerificationSnapshot.pan === "object"
                ? stage.customerVerificationSnapshot.pan
                : {}),
              number: stagePanNumber || undefined,
              verified: stagePanVerified,
            },
            panVerified: stagePanVerified ? "true" : "false",
          }),
        );
      })
      .catch(() => {});

    return () => {
      cancelled = true;
    };
  }, [applicationNumber, panNumber, dispatch]);

  useEffect(() => {
    if (!applicationNumber || panDateOfBirthFormatted) return;
    let cancelled = false;
    getJourneyData(applicationNumber)
      .then((journey) => {
        if (cancelled || !journey || typeof journey !== "object") return;
        // Backend returns .stages (parsed cumulative); Liferay raw may use cummulativeJourneyData string
        let stages = journey.stages;
        if (
          !stages &&
          (journey.cummulativeJourneyData ||
            journey.c_cummulativeJourneyData ||
            journey.cummulative_journey_data)
        ) {
          const raw =
            journey.cummulativeJourneyData ||
            journey.c_cummulativeJourneyData ||
            journey.cummulative_journey_data;
          if (typeof raw === "string") {
            try {
              stages = JSON.parse(raw);
            } catch {
              return;
            }
          } else {
            stages = raw;
          }
        }
        if (!stages || typeof stages !== "object") return;
        const merged = {
          ...(leadFormData || {}),
          ...mergeCumulativeStagesOrdered(stages),
        };
        merged.dateOfBirth =
          merged.dateOfBirth || merged.lifeAssuredDob || null;
        if (merged.dateOfBirth || merged.lifeAssuredDob) {
          dispatch(journeyActions.setLeadFormData(merged));
        }
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [applicationNumber, panDateOfBirthFormatted, leadFormData, dispatch]);

  useEffect(() => {
    let interval;

    if (timer > 0) {
      interval = setInterval(() => {
        setTimer((prev) => prev - 1);
      }, 1000);
    } else if (timer === 0 && aadhaarOtpSent) {
      setCanResend(true);
    }

    return () => clearInterval(interval);
  }, [timer, aadhaarOtpSent]);

  const getMaskedAadhaar = (value = "") => {
    const cleaned = sanitizeDigits(value);
    if (cleaned.length !== 12) return formatAadhaarNumber(cleaned);

    return `XXXX - XXXX - ${cleaned.slice(-4)}`;
  };

  const buildVerificationData = useCallback(() => {
    const cleanedAadhaar = sanitizeDigits(aadhaarNumber);
    const normalizedPanName = panName.trim().replace(/\s+/g, " ");
    const { givenName, surname } = splitPanName(normalizedPanName);
    const genderSource =
      leadFormData?.gender ||
      effectiveLeadResult?.leadInput?.lifeAssuredGender ||
      null;

    return {
      applicationNumber: applicationNumber || null,
      caseId: caseId || null,
      prefillData: {
        aadhaarNumber: cleanedAadhaar || null,
        panNumber: panNumber.trim().toUpperCase(),
        panName: normalizedPanName,
        panDateOfBirth: panDateOfBirthFormatted ?? null,
      },
      dedupData: {
        sex: normalizeSex(genderSource),
        givenName,
        surname,
        fatherName: null,
        spouseName: null,
      },
      currentStage: getCustomerVerificationStageByFlow(paymentFlowMode),
      // Sent for Customer Verification Object API (/o/c/customerverifications/)
      panVerified: "true",
      aadharVerified: aadhaarVerified ? "true" : "false",
      dedupStatus: "true",
      customerVerificationSnapshot: slimCustomerVerificationSnapshotForPersistence(
        cloneForJourney(customerVerificationDataFromStore),
      ),
    };
  }, [
    aadhaarNumber,
    applicationNumber,
    caseId,
    panNumber,
    panName,
    panDateOfBirthFormatted,
    leadFormData,
    effectiveLeadResult,
    aadhaarVerified,
    customerVerificationDataFromStore,
  ]);

  const validateForm = useCallback(() => {
    const cleanedAadhaar = sanitizeDigits(aadhaarNumber);
    const cleanedPan = panNumber.trim().toUpperCase();
    const cleanedPanName = panName.trim();

    if (cleanedAadhaar && !isValidAadhaar(cleanedAadhaar)) {
      setError("Please enter a valid Aadhaar number.");
      return false;
    }

    if (!cleanedPan) {
      setError("Please enter your PAN number.");
      return false;
    }
    if (!PAN_REGEX.test(cleanedPan)) {
      setError("Please enter a valid PAN number.");
      return false;
    }

    if (!cleanedPanName) {
      setError("Please enter your name as per PAN.");
      return false;
    }
    if (!NAME_REGEX.test(cleanedPanName)) {
      setError("Please enter a valid name as per PAN.");
      return false;
    }

    return true;
  }, [aadhaarNumber, panNumber, panName]);

  const handleResendOtp = () => {
    if (!canResend) return;

    handleSendOtp();
    setAadhaarOtp("");
  };

  const handleSendOtp = async () => {
    const cleaned = sanitizeDigits(aadhaarNumber);

    if (!isValidAadhaar(cleaned)) {
      setError("Please enter a valid Aadhaar number.");
      return;
    }
     setIsProcessing(true);
    try {
      const res = await generateAadhaarOtp(cleaned);

      const clientId = res?.data?.client_id;

      if (!clientId) {
        setError("Unable to initiate Aadhaar verification.");
        return;
      }

      dispatch(
        journeyActions.setCustomerVerificationData({
          aadhaar: {
            number: cleaned,
            clientId,
            verified: false,
          },
        }),
      );

      setAadhaarOtpSent(true);
      setTimer(60);
      setCanResend(false);
    } catch {
      setError("Failed to send OTP.");
    }finally{
       setIsProcessing(false);
    }
  };

  const handleVerifyAadhaar = async () => {
    if (!aadhaarOtp) {
      setError("Enter OTP.");
      return;
    }

    if (!aadhaarClientId) {
      setError("Session expired. Please resend OTP.");
      return;
    }
     setIsProcessing(true);
    try {
      const res = await verifyAadhaarOtp(aadhaarClientId, aadhaarOtp);

      if (!res?.data) {
        setError("Invalid OTP.");
        return;
      }

      setError(null);

      dispatch(
        journeyActions.setCustomerVerificationData({
          aadhaar: {
            number: sanitizeDigits(aadhaarNumber),
            clientId: aadhaarClientId,
            verified: true,
            response: res.data,
          },
        }),
      );
    } catch {
      setError("Aadhaar verification failed.");
    }finally{
       setIsProcessing(false);
    }
  };

  const handleVerifyPan = async () => {

    if (!panDateOfBirthFormatted) {
      setError(
        "Date of birth is required for PAN verification. It is taken from your application details. " +
          "If you have just returned from payment, please wait a moment for your details to load, or go back to the lead step and complete your profile.",
      );
      return;
    }
    setIsProcessing(true);
    const verificationData = buildVerificationData();

    try {
      const res = await verifyPanDetails(verificationData);

      const panPassed = res?.nsdl === true && normalizeBooleanLike(res?.nextStep);
      if (panPassed) {
        dispatch(
          journeyActions.setCustomerVerificationData({
            pan: {
              number: panNumber.trim().toUpperCase(),
              name: panName.trim().toUpperCase(),
              verified: true,
              response: res,
            },
          }),
        );
        setPanVerified(true);
      } else {
        setError(resolvePanVerificationMessage(res));
      }
    } catch (err) {
      setError(resolvePanVerificationMessage(null, err));
    }finally{
       setIsProcessing(false);
    }
  };

  const handleContinue = useCallback(async () => {
    setError(null);

    if (!validateForm()) return;

    if (!panVerified) {
      setError("Please verify PAN before continuing.");
      return;
    }

    setIsProcessing(true);

    const verificationData = buildVerificationData();
    const stage = getCustomerVerificationStageByFlow(paymentFlowMode);

    try {
      if (applicationNumber && caseId && stage) {
        await updateJourneyStage(
          applicationNumber,
          caseId,
          stage,
          verificationData,
        );
      }
      handleCustomerVerificationComplete?.(verificationData);
      navigate("/proposal");
    } catch (err) {
      setError(err?.message || "Failed to save progress. Please try again.");
    } finally {
      setIsProcessing(false);
    }
  }, [
    validateForm,
    panVerified,
    buildVerificationData,
    handleCustomerVerificationComplete,
    navigate,
    applicationNumber,
    caseId,
    paymentFlowMode,
  ]);

  if (isResuming || isProcessing) {
    return (
      <div
        className="quote-premium-loader-overlay"
        aria-live="polite"
        aria-busy="true"
      >
        <BrandLoader />
      </div>
    );
  }

  return (
    <div className="customer-verification">
      <header className="verification-header">
        <div className="quote-header-wraper">
          <div className="header-left" onClick={() =>  window.location.href = "/home"}>
            <img src={logo} alt="" width="120" height="56" />
          </div>
          <button
            className="help-button btn secondary-button regular"
            onClick={() => setShowHelpModal(true)}
          >
            <svg
              width="20"
              height="21"
              viewBox="0 0 20 21"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M16.6772 2.87806C15.3151 1.50799 13.5769 0.573101 11.683 0.191867C9.789 -0.189367 7.82449 0.000214264 6.03838 0.736587C4.25227 1.47296 2.72496 2.72298 1.64998 4.32823C0.575006 5.93349 0.000755732 7.82174 0 9.75369V15.0037C0 15.6004 0.237053 16.1727 0.65901 16.5947C1.08097 17.0166 1.65326 17.2537 2.25 17.2537H3.75C4.34674 17.2537 4.91903 17.0166 5.34099 16.5947C5.76295 16.1727 6 15.6004 6 15.0037V11.2537C6 10.657 5.76295 10.0847 5.34099 9.6627C4.91903 9.24074 4.34674 9.00369 3.75 9.00369H1.53375C1.67839 7.43988 2.26578 5.94969 3.22709 4.70779C4.18839 3.46588 5.48377 2.52372 6.96141 1.99172C8.43904 1.45972 10.0377 1.35993 11.57 1.70404C13.1024 2.04815 14.5048 2.82191 15.6131 3.93463C16.9677 5.29616 17.8009 7.09031 17.9672 9.00369H15.75C15.1533 9.00369 14.581 9.24074 14.159 9.6627C13.7371 10.0847 13.5 10.657 13.5 11.2537V15.0037C13.5 15.6004 13.7371 16.1727 14.159 16.5947C14.581 17.0166 15.1533 17.2537 15.75 17.2537H18C18 17.8504 17.7629 18.4227 17.341 18.8447C16.919 19.2666 16.3467 19.5037 15.75 19.5037H10.5C10.3011 19.5037 10.1103 19.5827 9.96967 19.7234C9.82902 19.864 9.75 20.0548 9.75 20.2537C9.75 20.4526 9.82902 20.6434 9.96967 20.784C10.1103 20.9247 10.3011 21.0037 10.5 21.0037H15.75C16.7446 21.0037 17.6984 20.6086 18.4017 19.9053C19.1049 19.2021 19.5 18.2483 19.5 17.2537V9.75369C19.5049 8.47829 19.2581 7.21446 18.7737 6.03462C18.2893 4.85477 17.5768 3.7821 16.6772 2.87806ZM3.75 10.5037C3.94891 10.5037 4.13968 10.5827 4.28033 10.7234C4.42098 10.864 4.5 11.0548 4.5 11.2537V15.0037C4.5 15.2026 4.42098 15.3934 4.28033 15.534C4.13968 15.6747 3.94891 15.7537 3.75 15.7537H2.25C2.05109 15.7537 1.86032 15.6747 1.71967 15.534C1.57902 15.3934 1.5 15.2026 1.5 15.0037V10.5037H3.75ZM15.75 15.7537C15.5511 15.7537 15.3603 15.6747 15.2197 15.534C15.079 15.3934 15 15.2026 15 15.0037V11.2537C15 11.0548 15.079 10.864 15.2197 10.7234C15.3603 10.5827 15.5511 10.5037 15.75 10.5037H18V15.7537H15.75Z"
                fill="#97262A"
              />
            </svg>
            Need Help?
          </button>
        </div>
      </header>

      <div className="verification-content">
        <div className="prefill-layout">
          <div className="prefill-card">
            <div className="aadhar-verification">
              <div className="header-content">
                <p className="application-number">
                  Application Number: <span>{applicationNumber || "-"}</span>
                </p>
              </div>
              <h6 className="section-title m-0">
                Make upcoming forms quicker with smart pre-fill
              </h6>
              <p className="section-subtitle">
                Share your Aadhaar details to auto-fill proposer information in
                the next steps.
              </p>
              <div className="field-row">
                <label htmlFor="aadhaarNumber" className="field-label selected">
                  <div className="label-groups">
                    <p>
                      <span>Aadhaar Card</span>
                      <span className="field-optional">
                        Helps with your residential details
                      </span>
                    </p>
                  </div>

                  <input
                    id="aadhaarNumber"
                    disabled={aadhaarVerified}
                    type="text"
                    className="verification-input"
                    inputMode="numeric"
                    placeholder="0000 - 0000 - 0000"
                    autoComplete="off"
                    value={aadhaarVerified? getMaskedAadhaar(aadhaarNumber) : formatAadhaarNumber(aadhaarNumber)}
                    onChange={(e) => {
                      const newValue = sanitizeDigits(e.target.value).slice(0, 12);
                      setAadhaarNumber(newValue);
                      setError(null);
                      if (aadhaarOtpSent) {
                        setAadhaarOtpSent(false);
                        setAadhaarOtp("");
                        dispatch(journeyActions.setCustomerVerificationData({
                          aadhaar: {
                            number: newValue,
                            clientId: null,
                            verified: false,
                          },
                        })
                        );
                      }
                    }}
                  />
                </label>
                {aadhaarOtpSent && !aadhaarVerified && (
                  <div className="otp-section">
                    <input
                      type="password"
                      inputMode="numeric"
                      maxLength={6}
                      placeholder="Enter OTP"
                      value={aadhaarOtp}
                      onChange={(e) => setAadhaarOtp(e.target.value)}
                    />
                    <div className="resend-otp-text" style={{ marginTop: "6px" }}>
                      {canResend ? (
                        <span
                          onClick={handleResendOtp}
                          style={{
                            color: "#97262A",
                            cursor: "pointer",
                            fontWeight: 500,
                          }}
                        >
                          Resend OTP
                        </span>
                      ) : (
                        <span style={{ color: "#888", fontSize: "13px" }}>
                          Resend OTP in {timer}s
                        </span>
                      )}
                    </div>
                  </div>
                )}
                <button
                  className="btn secondary-button ml-auto"
                  disabled={aadhaarVerified}
                  onClick={() => {
                    if (!aadhaarOtpSent) {
                      handleSendOtp();
                    } else {
                      handleVerifyAadhaar();
                    }
                  }}
                >
                  {aadhaarVerified
                    ? "Aadhaar Verified ✓"
                    : aadhaarOtpSent
                      ? "Verify OTP"
                      : "Send OTP"}
                </button>
              </div>
              <div className="security-note">
                <span>🔐</span>
                Aadhaar verification is done securely using UIDAI-approved
                services. Your details are masked and never stored.
              </div>
            </div>
            <div className="pan-card">
              <h2 className="section-title">
                Enter your PAN Details (Mandatory)
              </h2>
              <div className="pan-grid">
                <div className="input-group">
                  <label htmlFor="panNumber" className="label-one m-0">
                    PAN Number
                  </label>
                  <input
                    id="panNumber"
                    disabled={panVerified}
                    type="text"
                    className="verification-input"
                    placeholder="AAAPA1234A"
                    value={panNumber}
                    autoComplete="off"
                    onChange={(e) => {
                      let value = e.target.value.toUpperCase();
                      value = value.replace(/[^A-Z0-9]/g, "").slice(0, 10);
                      setPanNumber(value);
                      if (value.length === 10 && !PAN_REGEX.test(value)) {
                        setError("Please enter a valid PAN number.");
                      } else {
                        setError(null);
                      }
                    }}
                  />
                </div>
                <div className="input-group">
                  <label htmlFor="panName" className="label-one m-0">
                    Name (as per PAN)
                  </label>
                  <input
                    id="panName"
                    name="random_pan_field"
                    disabled={panVerified}
                    type="text"
                    className="verification-input"
                    placeholder="Enter full name"
                    value={panName}
                    autoComplete="off"
                    spellCheck="false"
                    autoCorrect="off"
                    autoCapitalize="none"
                    onChange={(e) => {
                    let value = e.target.value;
                    value = value.replace(/[^a-zA-Z\s]/g, "");
                    value = value.replace(/\s+/g, " ");
                    if (value.startsWith(" ")) return;
                    setPanName(value);
                    setError(null);
                    }} 
                    maxLength={100}
                  />
                </div>
                <button
                  className="btn secondary-button ml-auto"
                  onClick={handleVerifyPan}
                  disabled={!isPanValid || panVerified}
                >
                  {panVerified ? "PAN Verified ✓" : "Verify PAN"}
                </button>
              </div>
            </div>
          </div>

          <aside className="prefill-benefits-card">
            <div className="aside-wraper">
              <h3 className="benefits-title">
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 20 20"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M7.91602 18.3346H12.0827M12.4993 12.7733C14.4701 11.8371 15.8327 9.8283 15.8327 7.5013C15.8327 4.27964 13.221 1.66797 9.99935 1.66797C6.77769 1.66797 4.16602 4.27964 4.16602 7.5013C4.16602 9.8283 5.52856 11.8371 7.49935 12.7733V13.3346C7.49935 14.1112 7.49935 14.4995 7.62622 14.8058C7.79537 15.2142 8.11983 15.5386 8.52821 15.7078C8.8345 15.8346 9.22278 15.8346 9.99935 15.8346C10.7759 15.8346 11.1642 15.8346 11.4705 15.7078C11.8789 15.5386 12.2033 15.2142 12.3725 14.8058C12.4993 14.4995 12.4993 14.1112 12.4993 13.3346V12.7733Z"
                    stroke="#444446"
                    stroke-width="1.6"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                </svg>
                Here&apos;s why it&apos;s recommended to take this step.
              </h3>
              <ul className="benefits-list">
                <li>
                  <svg
                    width="17"
                    height="23"
                    viewBox="0 0 17 23"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M16.4847 10.3247C16.4563 10.2045 16.3987 10.0931 16.3169 10.0006C16.2351 9.908 16.1318 9.83707 16.016 9.79406L10.615 7.76812L11.9894 0.893431C12.0205 0.7337 11.9989 0.568182 11.9277 0.421851C11.8565 0.27552 11.7396 0.156318 11.5947 0.082232C11.4498 0.00814581 11.2848 -0.0168043 11.1244 0.0111465C10.9641 0.0390974 10.8173 0.118433 10.706 0.237181L0.205988 11.4872C0.120708 11.577 0.0590157 11.6866 0.0264224 11.8061C-0.0061709 11.9257 -0.00865008 12.0514 0.0192066 12.1721C0.0470632 12.2928 0.104388 12.4048 0.186059 12.4979C0.267731 12.5911 0.371205 12.6625 0.487238 12.7059L5.89005 14.7319L4.51943 21.5991C4.4883 21.7588 4.50998 21.9243 4.58118 22.0706C4.65238 22.217 4.76925 22.3362 4.91414 22.4103C5.05903 22.4843 5.22409 22.5093 5.3844 22.4813C5.54472 22.4534 5.69159 22.3741 5.80286 22.2553L16.3029 11.0053C16.3866 10.9154 16.447 10.8064 16.4787 10.6877C16.5104 10.569 16.5125 10.4443 16.4847 10.3247ZM6.50786 19.3087L7.48942 14.3981C7.52456 14.224 7.49672 14.043 7.41087 13.8874C7.32503 13.7319 7.18675 13.6119 7.02067 13.5487L2.06693 11.6878L10 3.18843L9.01943 8.09906C8.9843 8.27321 9.01213 8.45419 9.09798 8.60974C9.18382 8.76529 9.3221 8.88531 9.48818 8.94843L14.4382 10.8047L6.50786 19.3087Z"
                      fill="#AE272C"
                    />
                  </svg>

                  <span>
                    {" "}
                    Automatically fills proposer details in the upcoming form
                  </span>
                </li>
                <li>
                  <svg
                    width="17"
                    height="18"
                    viewBox="0 0 17 18"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M16.2604 17.8117C16.1132 17.9454 15.9189 18.0152 15.7203 18.0057C15.5216 17.9962 15.3348 17.9082 15.201 17.761L9.00605 10.9463V15.0067H11.256C11.455 15.0067 11.6457 15.0857 11.7864 15.2263C11.927 15.367 12.006 15.5577 12.006 15.7567C12.006 15.9556 11.927 16.1463 11.7864 16.287C11.6457 16.4276 11.455 16.5067 11.256 16.5067H5.25605C5.05714 16.5067 4.86637 16.4276 4.72572 16.287C4.58507 16.1463 4.50605 15.9556 4.50605 15.7567C4.50605 15.5577 4.58507 15.367 4.72572 15.2263C4.86637 15.0857 5.05714 15.0067 5.25605 15.0067H7.50605V9.29634L2.25605 3.52134V5.25665C2.25605 5.45557 2.17703 5.64633 2.03638 5.78698C1.89573 5.92764 1.70496 6.00665 1.50605 6.00665C1.30714 6.00665 1.11637 5.92764 0.975718 5.78698C0.835066 5.64633 0.756048 5.45557 0.756048 5.25665V2.25665C0.75614 2.14959 0.779156 2.04378 0.823548 1.94634L0.201048 1.26103C0.133423 1.18839 0.0808885 1.10304 0.0464895 1.00995C0.0120905 0.916851 -0.00348926 0.817853 0.000653776 0.718692C0.00479681 0.61953 0.0285803 0.522177 0.0706251 0.432275C0.11267 0.342373 0.17214 0.261709 0.245589 0.19496C0.319038 0.128211 0.405005 0.0767035 0.498506 0.0434231C0.592008 0.0101427 0.691186 -0.00424861 0.79029 0.00108303C0.889395 0.00641467 0.986456 0.0313634 1.07585 0.0744831C1.16524 0.117603 1.24518 0.178036 1.31105 0.25228L16.311 16.7523C16.4448 16.8995 16.5146 17.0938 16.5051 17.2924C16.4956 17.4911 16.4076 17.6779 16.2604 17.8117ZM6.17386 3.00665H7.50605V4.54697C7.50605 4.74588 7.58507 4.93665 7.72572 5.0773C7.86637 5.21795 8.05714 5.29697 8.25605 5.29697C8.45496 5.29697 8.64573 5.21795 8.78638 5.0773C8.92703 4.93665 9.00605 4.74588 9.00605 4.54697V3.00665H14.256V5.25665C14.256 5.45557 14.3351 5.64633 14.4757 5.78698C14.6164 5.92764 14.8071 6.00665 15.006 6.00665C15.205 6.00665 15.3957 5.92764 15.5364 5.78698C15.677 5.64633 15.756 5.45557 15.756 5.25665V2.25665C15.756 2.05774 15.677 1.86698 15.5364 1.72632C15.3957 1.58567 15.205 1.50665 15.006 1.50665H6.17386C5.97495 1.50665 5.78418 1.58567 5.64353 1.72632C5.50288 1.86698 5.42386 2.05774 5.42386 2.25665C5.42386 2.45557 5.50288 2.64633 5.64353 2.78698C5.78418 2.92764 5.97495 3.00665 6.17386 3.00665Z"
                      fill="#AE272C"
                    />
                  </svg>

                  <span>Reduces manual typing and errors</span>
                </li>
                <li>
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 20 20"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M9.75 0C7.82164 0 5.93657 0.571828 4.33319 1.64317C2.72982 2.71451 1.48013 4.23726 0.742179 6.01884C0.00422452 7.80042 -0.188858 9.76082 0.187348 11.6521C0.563554 13.5434 1.49215 15.2807 2.85571 16.6443C4.21928 18.0079 5.95656 18.9365 7.84787 19.3127C9.73919 19.6889 11.6996 19.4958 13.4812 18.7578C15.2627 18.0199 16.7855 16.7702 17.8568 15.1668C18.9282 13.5634 19.5 11.6784 19.5 9.75C19.4973 7.16498 18.4692 4.68661 16.6413 2.85872C14.8134 1.03084 12.335 0.00272983 9.75 0ZM9.75 18C8.11831 18 6.52326 17.5161 5.16655 16.6096C3.80984 15.7031 2.75242 14.4146 2.128 12.9071C1.50358 11.3996 1.3402 9.74085 1.65853 8.1405C1.97685 6.54016 2.76259 5.07015 3.91637 3.91637C5.07016 2.76259 6.54017 1.97685 8.14051 1.65852C9.74085 1.34019 11.3997 1.50357 12.9071 2.12799C14.4146 2.75242 15.7031 3.80984 16.6096 5.16655C17.5161 6.52325 18 8.1183 18 9.75C17.9975 11.9373 17.1275 14.0343 15.5809 15.5809C14.0343 17.1275 11.9373 17.9975 9.75 18ZM15.75 9.75C15.75 9.94891 15.671 10.1397 15.5303 10.2803C15.3897 10.421 15.1989 10.5 15 10.5H9.75C9.55109 10.5 9.36033 10.421 9.21967 10.2803C9.07902 10.1397 9 9.94891 9 9.75V4.5C9 4.30109 9.07902 4.11032 9.21967 3.96967C9.36033 3.82902 9.55109 3.75 9.75 3.75C9.94892 3.75 10.1397 3.82902 10.2803 3.96967C10.421 4.11032 10.5 4.30109 10.5 4.5V9H15C15.1989 9 15.3897 9.07902 15.5303 9.21967C15.671 9.36032 15.75 9.55109 15.75 9.75Z"
                      fill="#AE272C"
                    />
                  </svg>
                  <span>Makes the proposal process quicker and smoother</span>
                </li>
              </ul>
              <p className="benefits-note">
                You can skip this step and enter details manually if you prefer.
              </p>
            </div>
          </aside>
        </div>
      </div>

      <section className="quote-submit-section">
        {error && (
          <div
            className="error-message error-message-inline"
            role="alert"
            aria-live="assertive"
          >
            {error}
          </div>
        )}

        <div className="quote-submit-strip">
          <button
            className="btn-submit-quote btn primary-button regular ml-auto"
            onClick={handleContinue}
            disabled={isProcessing || !panVerified}
            type="button"
          >
            {isProcessing ? "Processing..." : "Continue"}
          </button>
        </div>
      </section>

      <NeedhelpModal
        isOpen={showHelpModal}
        onClose={() => setShowHelpModal(false)}
        title="Need Help?"
        type="success"
      />
    </div>
  );
};

export default CustomerVerification;
